import { ReactNode, useEffect, useState } from 'react'

const SpinnerVariants = {
    Wheel: ({ width = 96, height = 96, className = '' }) => (
        <div className={`relative ${className}`} style={{ width, height }}>
            <div className='absolute inset-0 border-4 rounded-full border-white/20'></div>
            <div className='absolute inset-0 border-4 border-transparent rounded-full border-t-cyan-400 animate-spin'></div>
            <div
                className='absolute border-2 border-transparent rounded-full inset-2 border-t-yellow-400 animate-spin'
                style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}
            ></div>
        </div>
    ),

    Bars: ({ width = 24, height = 24, className = '' }) => (
        <div className={`flex items-end space-x-1 ${className}`} style={{ width, height }}>
            {[0, 1, 2].map((i) => (
                <div
                    key={i}
                    className='rounded-sm bg-gradient-to-t from-cyan-400 to-yellow-400 animate-pulse'
                    style={{
                        width: width / 5,
                        height: height / 3,
                        animationDelay: `${i * 0.15}s`,
                        animationDuration: '0.8s',
                    }}
                ></div>
            ))}
        </div>
    ),

    Dots: ({ width = 96, height = 96, className = '' }) => (
        <div className={`relative ${className}`} style={{ width, height }}>
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div
                    key={i}
                    className='absolute w-3 h-3 rounded-full bg-gradient-to-r from-cyan-400 to-yellow-400 animate-pulse'
                    style={{
                        top: '50%',
                        left: '50%',
                        transform: `rotate(${i * 45}deg) translateY(-${width / 3}px)`,
                        transformOrigin: '0 0',
                        animationDelay: `${i * 0.1}s`,
                        animationDuration: '1.2s',
                    }}
                ></div>
            ))}
        </div>
    ),

    Pulse: ({ width = 96, height = 96, className = '' }) => (
        <div className={`relative ${className}`} style={{ width, height }}>
            <div className='absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400 to-yellow-400 animate-ping opacity-20'></div>
            <div
                className='absolute rounded-full inset-2 bg-gradient-to-r from-cyan-300 to-yellow-300 animate-ping opacity-40'
                style={{ animationDelay: '0.2s' }}
            ></div>
            <div
                className='absolute rounded-full inset-4 bg-gradient-to-r from-cyan-200 to-yellow-200 animate-ping opacity-60'
                style={{ animationDelay: '0.4s' }}
            ></div>
        </div>
    ),
}

type LoadingStep = {
    message: string
    duration?: number
    icon?: string
}

type InteractiveLoadingProps = {
    isLoading?: boolean
    variant?: keyof typeof SpinnerVariants
    size?: number
    mode?: 'inline' | 'block'
    className?: string
    spinnerClassName?: string
    textClassName?: string
    textPosition?: 'left' | 'right' | 'top' | 'bottom'
    children?: ReactNode
    steps?: LoadingStep[]
    currentStep?: number
    autoAdvance?: boolean
    defaultStepDuration?: number
    onStepChange?: (stepIndex: number, step: LoadingStep) => void
    onComplete?: () => void
    showProgress?: boolean
    theme?: 'dark' | 'light' | 'brightpath'
}

const spacingMap = {
    inline: {
        horizontal: 'gap-x-3',
        vertical: 'gap-y-2',
    },
    block: {
        horizontal: 'gap-x-8',
        vertical: 'gap-y-8',
    },
}

const directionMap = {
    left: 'flex-row-reverse',
    right: 'flex-row',
    top: 'flex-col-reverse',
    bottom: 'flex-col',
}

const defaultSteps: LoadingStep[] = [
    { message: 'Saving your information...', duration: 1200 },
    { message: 'Generating your personalized roadmap...', duration: 1800 },
    { message: 'Finalizing your BrightPath journey...', duration: 2200 },
    { message: 'Almost ready...', duration: 1500 },
]

const themeStyles = {
    dark: {
        container: 'bg-white/15 backdrop-blur-xl border border-white/20',
        text: 'text-white',
        subtext: 'text-white/70',
        progress: 'bg-white/20',
        progressFill: 'bg-gradient-to-r from-yellow-400 to-white',
    },
    light: {
        container: 'bg-white/90 backdrop-blur-xl border border-white/30 shadow-2xl',
        text: 'text-cyan-600',
        subtext: 'text-cyan-500',
        progress: 'bg-cyan-100',
        progressFill: 'bg-gradient-to-r from-cyan-500 to-yellow-400',
    },
    brightpath: {
        container: 'bg-white/15 backdrop-blur-xl border border-white/20',
        text: 'text-white',
        subtext: 'text-white/80',
        progress: 'bg-white/20',
        progressFill: 'bg-gradient-to-r from-yellow-400 to-white',
    },
}

function cn(...classes: (string | undefined | null | false)[]): string {
    return classes.filter(Boolean).join(' ')
}

export default function InteractiveLoading({
    isLoading = true,
    mode = 'block',
    variant = mode === 'inline' ? 'Bars' : 'Wheel',
    textPosition = mode === 'inline' ? 'right' : 'bottom',
    size,
    className,
    spinnerClassName,
    textClassName,
    children,
    steps = defaultSteps,
    currentStep: controlledCurrentStep,
    autoAdvance = true,
    defaultStepDuration = 2000,
    onStepChange,
    onComplete,
    showProgress = true,
    theme = 'brightpath',
}: InteractiveLoadingProps) {
    const [internalCurrentStep, setInternalCurrentStep] = useState(0)
    const [isCompleted, setIsCompleted] = useState(false)
    const [mounted, setMounted] = useState(false)

    const currentStep = controlledCurrentStep ?? internalCurrentStep
    const currentStepData = steps[currentStep]
    const progress = ((currentStep + 1) / steps.length) * 100

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (!isLoading || !autoAdvance || controlledCurrentStep !== undefined) return

        const timer = setTimeout(() => {
            if (currentStep < steps.length - 1) {
                const nextStep = currentStep + 1
                setInternalCurrentStep(nextStep)
                onStepChange?.(nextStep, steps[nextStep])
            } else if (!isCompleted) {
                setIsCompleted(true)
                onComplete?.()
            }
        }, currentStepData?.duration || defaultStepDuration)

        return () => clearTimeout(timer)
    }, [
        currentStep,
        steps,
        autoAdvance,
        controlledCurrentStep,
        defaultStepDuration,
        onStepChange,
        onComplete,
        isLoading,
        isCompleted,
        currentStepData,
    ])

    useEffect(() => {
        if (isLoading && controlledCurrentStep === undefined) {
            setInternalCurrentStep(0)
            setIsCompleted(false)
        }
    }, [isLoading, controlledCurrentStep])

    if (!isLoading) {
        return children ? <>{children}</> : null
    }

    if (!mounted) return null

    const defaultSize = mode === 'inline' ? 24 : 96
    const spinnerSize = size || defaultSize
    const direction = textPosition === 'left' || textPosition === 'right' ? 'horizontal' : 'vertical'
    const SelectedVariant = SpinnerVariants[variant]
    const styles = themeStyles[theme]

    if (mode === 'inline') {
        return (
            <div
                className={cn(
                    'inline-flex items-center',
                    directionMap[textPosition],
                    spacingMap[mode][direction],
                    className
                )}
            >
                <SelectedVariant
                    width={spinnerSize}
                    height={spinnerSize}
                    className={cn('flex-shrink-0', spinnerClassName)}
                />

                {currentStepData && (
                    <span className={cn('text-sm font-medium transition-all duration-300', styles.text, textClassName)}>
                        {currentStepData.message}
                    </span>
                )}
            </div>
        )
    }

    return (
        <div className={cn('fixed inset-0 flex items-center justify-center z-50', className)}>
            <div className='absolute inset-0 bg-black/20 backdrop-blur-sm' />

            <div
                className={cn(
                    'relative rounded-2xl p-12 max-w-md w-full mx-4',
                    'transform transition-all duration-500 ease-out',
                    mounted ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
                    styles.container
                )}
            >
                <div
                    className={cn(
                        'flex items-center justify-center',
                        directionMap[textPosition],
                        spacingMap[mode][direction]
                    )}
                >
                    <div className='relative'>
                        <SelectedVariant
                            width={spinnerSize}
                            height={spinnerSize}
                            className={cn('transform transition-transform duration-300', spinnerClassName)}
                        />
                    </div>

                    {currentStepData && (
                        <div className='space-y-6 text-center'>
                            <div className='space-y-3'>
                                {currentStepData.icon && (
                                    <div className='text-4xl animate-bounce' style={{ animationDuration: '2s' }}>
                                        {currentStepData.icon}
                                    </div>
                                )}

                                <div className='mt-6 text-center'>
                                    <h3 className='mb-2 text-xl font-semibold text-white'>
                                        Creating your personalized roadmap...
                                    </h3>
                                    <p className='max-w-md mx-auto text-base text-white/90'>
                                        We're building a custom journey map based on your child's unique needs.
                                        This may take up to 2 minutes - it'll be worth the wait!
                                    </p>
                                    <div className='flex items-center justify-center mt-4 space-x-2'>
                                        <div className='w-2 h-2 rounded-full bg-white/60 animate-pulse'></div>
                                        <div
                                            className='w-2 h-2 rounded-full bg-white/60 animate-pulse'
                                            style={{ animationDelay: '0.2s' }}
                                        ></div>
                                        <div
                                            className='w-2 h-2 rounded-full bg-white/60 animate-pulse'
                                            style={{ animationDelay: '0.4s' }}
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {showProgress && (
                                <div className='space-y-2'>
                                    <div className={cn('w-full h-2 rounded-full overflow-hidden', styles.progress)}>
                                        <div
                                            className={cn(
                                                'h-full rounded-full transition-all duration-500 ease-out',
                                                styles.progressFill
                                            )}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>

                                    <div className='flex justify-center space-x-2'>
                                        {steps.map((_, index) => (
                                            <div
                                                key={index}
                                                className={cn(
                                                    'w-2 h-2 rounded-full transition-all duration-300',
                                                    index <= currentStep
                                                        ? styles.progressFill.replace('bg-gradient-to-r', 'bg-blue-500')
                                                        : styles.progress
                                                )}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
